import {useBrandStore} from '../../shared/brand/brandStore';
import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  isSupported,
  onMessage,
  onRegistered,
  onUnregistered,
  register,
  unregister,
  type MessagePayload,
  type Messaging,
} from 'firebase/messaging';
import { apiFetch, authHeader } from '../../shared/api/httpClient';
import { clientHeaders } from '../../shared/lib/device';

const FID_STORAGE_KEY = 'sorteia:fcmInstallationId';
const NATIVE_TOKEN_STORAGE_KEY = 'sorteia:nativeFcmToken';
const NATIVE_ENABLED_STORAGE_KEY = 'sorteia:nativePushEnabled';
const FIREBASE_APP_NAME = 'sorteia-web-push';
declare const __SORTEIA_BUILD_ID__: string;

export type FirebasePushConfig = {
  enabled: boolean;
  configVersion: string;
  projectId?: string;
  apiKey?: string;
  appId?: string;
  messagingSenderId?: string;
  authDomain?: string;
  vapidKey?: string;
};

export type BrowserPushState = 'enabled' | 'disabled' | 'blocked' | 'unsupported' | 'unavailable';

type Runtime = {
  config: Required<Omit<FirebasePushConfig, 'authDomain'>> & { authDomain: string };
  app: FirebaseApp;
  messaging: Messaging;
  serviceWorker: ServiceWorkerRegistration;
};

let runtimePromise: Promise<Runtime> | null = null;
let runtimeVersion = '';
let foregroundUnsubscribe: (() => void) | null = null;

type NativePushBridge = {
  isSupported: () => boolean;
  packageName: () => string;
  permissionState: () => string;
  requestPermissionAndToken: () => void;
  requestToken: () => void;
};

type NativePushEvent = {
  state?: string;
  token?: string;
  packageName?: string;
  projectId?: string;
  error?: string;
};

declare global {
  interface Window {
    SorteiaNativePush?: NativePushBridge;
  }
}

export async function browserPushState(): Promise<BrowserPushState> {
  const native = nativeBridge();
  if (native) {
    if (!native.isSupported()) return 'unsupported';
    const permission = native.permissionState();
    if (permission === 'blocked') return 'blocked';
    return readNativeEnabled() && permission === 'granted' ? 'enabled' : 'disabled';
  }
  if (!(await supportsPush())) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  const config = await fetchConfig().catch(() => null);
  if (!config?.enabled) return 'unavailable';
  return readFid() && Notification.permission === 'granted' ? 'enabled' : 'disabled';
}

export async function enableFirebasePush(token: string): Promise<string> {
  const native = nativeBridge();
  if (native) {
    const config = await fetchConfig();
    if (!config.enabled || !config.projectId) throw new PushSetupError('PUSH_UNAVAILABLE');
    const registration = await requestNativeToken(native, true);
    await uploadNativeRegistration(token, config.projectId, registration);
    writeNativeRegistration(registration.token);
    return registration.token;
  }
  if (!(await supportsPush())) throw new PushSetupError('PUSH_UNSUPPORTED');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushSetupError('PUSH_PERMISSION_BLOCKED');
  const runtime = await getRuntime();
  return registerAndUpload(runtime, token);
}

/** Refreshes a granted browser's FID without ever opening a permission prompt. */
export async function syncFirebasePushIfGranted(token: string): Promise<void> {
  const native = nativeBridge();
  if (native) {
    if (!readNativeEnabled() || native.permissionState() !== 'granted') return;
    const config = await fetchConfig().catch(() => null);
    if (!config?.enabled || !config.projectId) return;
    const registration = await requestNativeToken(native, false);
    await uploadNativeRegistration(token, config.projectId, registration);
    writeNativeRegistration(registration.token);
    return;
  }
  if (!token || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (!(await supportsPush())) return;
  const config = await fetchConfig().catch(() => null);
  if (!config?.enabled) return;
  const runtime = await getRuntime(config);
  await registerAndUpload(runtime, token);
}

export async function disableFirebasePush(token: string): Promise<void> {
  const native = nativeBridge();
  if (native) {
    const savedToken = readNativeToken();
    if (savedToken) await removeRegistration(token, savedToken).catch(() => undefined);
    clearNativeRegistration();
    return;
  }
  const savedFid = readFid();
  if (!savedFid) return;
  try {
    if (await supportsPush()) {
      const config = await fetchConfig().catch(() => null);
      if (config?.enabled) {
        const runtime = await getRuntime(config);
        await unregister(runtime.messaging);
      }
    }
  } finally {
    await removeRegistration(token, savedFid).catch(() => undefined);
    clearFid();
  }
}

export async function installForegroundPushHandler(
  token: string,
  handler: (payload: MessagePayload) => void,
): Promise<() => void> {
  if (nativeBridge()) return () => undefined;
  if (!token || typeof Notification === 'undefined' || Notification.permission !== 'granted') return () => undefined;
  if (!(await supportsPush())) return () => undefined;
  const config = await fetchConfig().catch(() => null);
  if (!config?.enabled) return () => undefined;
  const runtime = await getRuntime(config);
  foregroundUnsubscribe?.();
  foregroundUnsubscribe = onMessage(runtime.messaging, handler);
  return () => {
    foregroundUnsubscribe?.();
    foregroundUnsubscribe = null;
  };
}

function nativeBridge(): NativePushBridge | null {
  if (typeof window === 'undefined') return null;
  const bridge = window.SorteiaNativePush;
  return bridge && typeof bridge.requestToken === 'function' ? bridge : null;
}

async function requestNativeToken(
  bridge: NativePushBridge,
  requestPermission: boolean,
): Promise<{ token: string; packageName: string; projectId: string }> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => finish(new PushSetupError('PUSH_REGISTRATION_TIMEOUT')), 20_000);
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<NativePushEvent>).detail || {};
      if (detail.token) {
        finish(null, {
          token: detail.token,
          packageName: detail.packageName || bridge.packageName(),
          projectId: detail.projectId || '',
        });
      } else if (detail.state === 'blocked') finish(new PushSetupError('PUSH_PERMISSION_BLOCKED'));
      else if (detail.state === 'unsupported') finish(new PushSetupError('PUSH_UNSUPPORTED'));
      else if (detail.error) finish(new PushSetupError(detail.error));
    };
    const finish = (
      error: unknown,
      registration?: { token: string; packageName: string; projectId: string },
    ) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      window.removeEventListener('sorteia:native-push', listener);
      if (error) reject(error);
      else if (registration) resolve(registration);
      else reject(new PushSetupError('PUSH_REGISTRATION_FAILED'));
    };
    window.addEventListener('sorteia:native-push', listener);
    try {
      if (requestPermission) bridge.requestPermissionAndToken();
      else bridge.requestToken();
    } catch (error) {
      finish(error);
    }
  });
}

async function uploadNativeRegistration(
  authToken: string,
  projectId: string,
  registration: { token: string; packageName: string; projectId: string },
) {
  if (registration.projectId && registration.projectId !== projectId) {
    throw new PushSetupError('FCM_PROJECT_MISMATCH');
  }
  await apiFetch('/api/push/registrations', {
    method: 'POST',
    headers: { ...clientHeaders(), ...authHeader(authToken) },
    body: JSON.stringify({
      installationId: registration.token,
      projectId,
      platform: 'android',
      packageName: registration.packageName,
    }),
  });
}

async function registerAndUpload(runtime: Runtime, token: string): Promise<string> {
  const installationId = await new Promise<string>((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => finish(new PushSetupError('PUSH_REGISTRATION_TIMEOUT')), 15_000);
    const stopRegistered = onRegistered(runtime.messaging, (fid) => finish(null, fid));
    const stopUnregistered = onUnregistered(runtime.messaging, (fid) => {
      void removeRegistration(token, fid).catch(() => undefined);
      if (readFid() === fid) clearFid();
    });

    const finish = (error: unknown, fid?: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      stopRegistered();
      // Keep the unregistration listener for this runtime. It is lightweight
      // and ensures subscription changes remove the server-side route.
      if (error) reject(error);
      else if (fid) resolve(fid);
      else reject(new PushSetupError('PUSH_REGISTRATION_FAILED'));
    };

    void register(runtime.messaging, {
      vapidKey: runtime.config.vapidKey,
      serviceWorkerRegistration: runtime.serviceWorker,
    }).catch((error) => {
      stopUnregistered();
      finish(error);
    });
  });

  await apiFetch('/api/push/registrations', {
    method: 'POST',
    headers: { ...clientHeaders(), ...authHeader(token) },
    body: JSON.stringify({ installationId, projectId: runtime.config.projectId }),
  });
  writeFid(installationId);
  return installationId;
}

async function getRuntime(config?: FirebasePushConfig): Promise<Runtime> {
  const requestedVersion = config?.configVersion || '';
  if (runtimePromise && requestedVersion && runtimeVersion && requestedVersion !== runtimeVersion) {
    // The public endpoint is no-store. A version bump therefore replaces the
    // in-page runtime and the service-worker URL without waiting for cache TTLs.
    runtimePromise = null;
  }
  if (runtimePromise) return runtimePromise;
  runtimeVersion = requestedVersion;
  runtimePromise = buildRuntime(config).catch((error) => {
    runtimePromise = null;
    runtimeVersion = '';
    throw error;
  });
  return runtimePromise;
}

async function buildRuntime(provided?: FirebasePushConfig): Promise<Runtime> {
  const config = provided || await fetchConfig();
  if (!config.enabled || !config.projectId || !config.apiKey || !config.appId || !config.messagingSenderId || !config.vapidKey) {
    throw new PushSetupError('PUSH_UNAVAILABLE');
  }
  runtimeVersion = config.configVersion || '1';
  const appName = `${FIREBASE_APP_NAME}-${String(config.configVersion || '1').replace(/[^A-Za-z0-9_-]/g, '_')}`;
  const app = getApps().some((candidate) => candidate.name === appName)
    ? getApp(appName)
    : initializeApp({
        apiKey: config.apiKey,
        authDomain: config.authDomain || undefined,
        projectId: config.projectId,
        appId: config.appId,
        messagingSenderId: config.messagingSenderId,
      }, appName);
  const workerVersion = `${config.configVersion || '1'}-${__SORTEIA_BUILD_ID__}`;
  const serviceWorker = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?v=${encodeURIComponent(workerVersion)}`,
    { scope: '/' },
  );
  return {
    config: {
      enabled: true,
      configVersion: config.configVersion || '1',
      projectId: config.projectId,
      apiKey: config.apiKey,
      appId: config.appId,
      messagingSenderId: config.messagingSenderId,
      authDomain: config.authDomain || '',
      vapidKey: config.vapidKey,
    },
    app,
    messaging: getMessaging(app),
    serviceWorker,
  };
}

function fetchConfig() {
  return apiFetch<FirebasePushConfig>('/api/config/push/firebase-config?v='+encodeURIComponent(useBrandStore.getState().cacheVersion));
}

function removeRegistration(token: string, installationId: string) {
  return apiFetch(`/api/push/registrations/${encodeURIComponent(installationId)}`, {
    method: 'DELETE',
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
}

async function supportsPush() {
  return typeof window !== 'undefined'
    && window.isSecureContext
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && await isSupported();
}

function readFid() {
  try {
    return window.localStorage.getItem(FID_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function writeFid(fid: string) {
  try {
    window.localStorage.setItem(FID_STORAGE_KEY, fid);
  } catch {
    // The registration is still valid server-side; it will be refreshed on the next session.
  }
}

function clearFid() {
  try {
    window.localStorage.removeItem(FID_STORAGE_KEY);
  } catch {
    // No local persistence to clear.
  }
}

function readNativeToken() {
  try {
    return window.localStorage.getItem(NATIVE_TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function readNativeEnabled() {
  try {
    return window.localStorage.getItem(NATIVE_ENABLED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeNativeRegistration(token: string) {
  try {
    window.localStorage.setItem(NATIVE_TOKEN_STORAGE_KEY, token);
    window.localStorage.setItem(NATIVE_ENABLED_STORAGE_KEY, '1');
  } catch {
    // The backend registration is authoritative; the next explicit toggle can refresh it.
  }
}

function clearNativeRegistration() {
  try {
    window.localStorage.removeItem(NATIVE_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(NATIVE_ENABLED_STORAGE_KEY);
  } catch {
    // No local persistence to clear.
  }
}

export class PushSetupError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'PushSetupError';
  }
}
